import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProposedColumns, getSavedColumns, saveColumns } from '../../api/captureTemplates';
import { captureAPI } from '../../services/api';
import { FIELD_OPTIONS, optionKeyForColumn, applyFieldSelection, shouldShowGateWarning } from './lineItemColumnsFields';
import './LineItemColumnsPanel.css';

function fromProposed(row) {
  return {
    column_order: row.column_order,
    label: row.label || '',
    x_min: row.x_min ?? null,
    x_max: row.x_max ?? null,
    mapped_field_key: row.suggested_field_key ?? null,
    data_type: row.suggested_data_type || 'text',
    is_custom: false,
  };
}

function fromSaved(row) {
  return {
    column_order: row.column_order,
    label: row.label || '',
    x_min: row.x_min ?? null,
    x_max: row.x_max ?? null,
    mapped_field_key: row.mapped_field_key ?? null,
    data_type: row.data_type || 'text',
    is_custom: !!row.is_custom,
  };
}

function readErr(e) {
  return e?.response?.data?.error || e?.message || 'Something went wrong';
}

const EMPTY_HELP =
  "We couldn't detect columns — make sure the table zone covers the line-item grid, including its header row.";

export default function LineItemColumnsPanel({ templateId, zoneId }) {
  const [columns, setColumns] = useState([]);
  const [mode, setMode] = useState('loading'); // loading | saved | proposed | empty | error
  const [reason, setReason] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [gateOff, setGateOff] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setMode('loading');
      setError(null);
      setReason(null);
      setSavedOk(false);
      // Read the tenant gate concurrently with the column load. Fail OPEN: a
      // settings error never blocks the panel or shows a (possibly wrong) warning.
      const settingsP = captureAPI.getCaptureSettings()
        .then(res => res?.settings)
        .catch(err => { console.warn('[LineItemColumnsPanel] settings read failed (gate warning suppressed):', err?.message); return null; });
      settingsP.then(s => { if (!cancelled) setGateOff(shouldShowGateWarning(s)); });
      try {
        // Saved beats proposed — never re-propose over confirmed work.
        const saved = await getSavedColumns(templateId, zoneId);
        if (cancelled) return;
        if (saved?.columns?.length) {
          setColumns(saved.columns.map(fromSaved));
          setMode('saved');
          return;
        }
        const proposed = await getProposedColumns(templateId, zoneId);
        if (cancelled) return;
        if (proposed?.columns?.length) {
          setColumns(proposed.columns.map(fromProposed));
          setMode('proposed');
        } else {
          setColumns([]);
          setReason(proposed?.reason || null);
          setMode('empty');
        }
      } catch (e) {
        if (!cancelled) {
          setError(readErr(e));
          setMode('error');
        }
      }
    }
    if (templateId && zoneId) load();
    return () => { cancelled = true; };
  }, [templateId, zoneId]);

  function handleSelect(index, optionKey) {
    setColumns(prev => prev.map((c, i) => (i === index ? applyFieldSelection(c, optionKey) : c)));
    setSavedOk(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSavedOk(false);
    try {
      const payload = columns.map(c => ({
        column_order: c.column_order,
        x_min: c.x_min,
        x_max: c.x_max,
        label: c.label,
        mapped_field_key: c.mapped_field_key,
        data_type: c.data_type,
        is_custom: c.is_custom,
      }));
      const res = await saveColumns(templateId, zoneId, payload);
      setColumns((res?.columns || []).map(fromSaved));
      setMode('saved');
      setSavedOk(true);
    } catch (e) {
      setError(readErr(e));
    } finally {
      setSaving(false);
    }
  }

  // Non-blocking warning: the account setting is off. A vendor profile's
  // line_items_mode overrides it ('on' still extracts), and 'follow' or no
  // profile uses it, so the notice names both controls. Read-only: no tenant
  // write from the studio. No vendor lookup: a template has no vendor link.
  const gateBanner = gateOff ? (
    <div className="li-columns-gate-warning" role="alert">
      Line items are turned off for your account, so documents from vendors set to Follow, or with no
      vendor profile, will skip them. A vendor set to On in{' '}
      <Link to="/vendors" className="li-columns-gate-link">Vendor Profiles</Link> still extracts them.{' '}
      Turn line items on for the account in <Link to="/settings/capture" className="li-columns-gate-link">Settings</Link>.
    </div>
  ) : null;

  if (mode === 'loading') {
    return (
      <div className="zone-field-mapping li-columns-panel">
        {gateBanner}
        <p className="zone-info-note">Detecting columns…</p>
      </div>
    );
  }

  if (mode === 'error') {
    return (
      <div className="zone-field-mapping li-columns-panel">
        {gateBanner}
        <p className="li-columns-error">{error}</p>
      </div>
    );
  }

  if (mode === 'empty') {
    return (
      <div className="zone-field-mapping li-columns-panel">
        {gateBanner}
        <p className="zone-info-note">{EMPTY_HELP}</p>
        {reason && <p className="li-columns-reason">{reason}</p>}
      </div>
    );
  }

  return (
    <div className="zone-field-mapping li-columns-panel">
      {gateBanner}
      <label className="mapping-label">Table Columns</label>
      <p className="li-columns-sub">
        {mode === 'saved' ? 'Saved column mapping.' : 'Detected columns — confirm each maps to the right field, then save.'}
      </p>

      <div className="li-columns-list">
        {columns.map((col, i) => (
          <div className="li-column-row" key={col.column_order}>
            <span className="li-column-label" title={col.label}>{col.label || `Column ${col.column_order + 1}`}</span>
            <select
              className="mapping-select li-column-select"
              value={optionKeyForColumn(col)}
              onChange={(e) => handleSelect(i, e.target.value)}
              disabled={saving}
            >
              {FIELD_OPTIONS.map(o => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {error && <p className="li-columns-error">{error}</p>}

      <div className="li-columns-actions">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saving || columns.length === 0}
        >
          {saving ? 'Saving…' : 'Save Columns'}
        </button>
        {savedOk && <span className="li-columns-saved">✓ Saved</span>}
      </div>
    </div>
  );
}
